import React from 'react';
import { Link } from 'react-router-dom';
import { PartnerResourceConfig } from './partner-resource-config';
import { ROUTE_PATH } from '../../../routes/route-paths';
import { ProviderName } from '../../../hooks/api/useExternalProviderPlans';
import { Resource } from './useResourceModalData';

interface ResourceModalCtaButtonsProps {
  resource: Resource;
  isProviderResource: boolean;
  isGlobal: boolean;
  finalDetectedProviderName: ProviderName | undefined;
  partnerResourceConfig: PartnerResourceConfig | undefined;
  ssoLinkLoading: boolean;
  url: string | undefined;
  errorMessage: string | undefined;
  urlError: boolean;
  displayPhoneNumber: boolean;
  onCallClick: () => void;
  onLinkClick: () => void;
}

export const ResourceModalCtaButtons: React.FC<ResourceModalCtaButtonsProps> = ({
  resource,
  isProviderResource,
  isGlobal,
  finalDetectedProviderName,
  partnerResourceConfig,
  ssoLinkLoading,
  url,
  errorMessage,
  urlError,
  displayPhoneNumber,
  onCallClick,
  onLinkClick,
}) => {
  // Handle relative resource URLs like `google.com`
  const validPath = resource?.url?.includes('.')
    ? resource.url.startsWith('http://') || resource.url.startsWith('https://')
      ? resource.url
      : `https://${resource.url}`
    : resource?.url;

  const renderProviderCta = () => {
    if (!partnerResourceConfig) return null;

    if (partnerResourceConfig.needsAddress) {
      return (
        <div className="flex flex-col items-center">
          <div className="text-sm font-medium leading-tight text-gray-800">
            We need your home address in order to activate this service.
          </div>
          <div className="mt-3">
            <Link to={ROUTE_PATH.SETTINGS_PROFILE}>
              <button className="font-albert-sans flex h-10 cursor-pointer items-center justify-center rounded-full border border-gray-300 bg-white px-7 py-2.5 text-base font-medium leading-5 text-gray-800 transition-colors hover:bg-gray-50">
                Add Address
              </button>
            </Link>
          </div>
        </div>
      );
    }

    if (partnerResourceConfig.waitForProcessingDate) {
      return (
        <div className="text-base font-medium leading-tight text-gray-800">
          You will have access to {resource?.name} beginning on {partnerResourceConfig.processingDateString}
        </div>
      );
    }

    if (errorMessage || urlError) {
      if (finalDetectedProviderName === 'opyn') {
        return (
          <div className="text-base font-medium leading-tight text-gray-800">
            Your access to {resource?.name} isn't active yet — please try again tomorrow.
          </div>
        );
      } else {
        return (
          <div className="text-base font-medium leading-tight text-gray-800">
            Your access to {resource?.name} isn't active — please contact your plan administrator.
          </div>
        );
      }
    }

    return (
      <a href={url} target="_blank" rel="noopener noreferrer" onClick={onLinkClick}>
        <button
          disabled={ssoLinkLoading}
          className="font-albert-sans bg-button-primary hover:bg-button-primary-hover flex h-10 cursor-pointer items-center justify-center rounded-full px-7 py-2.5 text-base font-medium leading-5 text-white shadow-[0px_1px_0.5px_0.05px_rgba(29,41,61,0.02)] transition-colors disabled:cursor-not-allowed disabled:opacity-50"
        >
          {ssoLinkLoading ? 'Loading...' : 'Visit Online'}
        </button>
      </a>
    );
  };

  const renderDefaultCta = () => {
    if (!resource?.url) return null;

    return (
      <a
        href={validPath}
        target="_blank"
        rel="noreferrer"
        aria-label={(resource as any).url_description || resource.url}
        onClick={onLinkClick}
      >
        <button className="font-albert-sans bg-button-primary hover:bg-button-primary-hover flex h-10 min-w-36 cursor-pointer items-center justify-center rounded-full px-7 py-2.5 text-base font-medium leading-5 text-white transition-colors">
          Visit Online
        </button>
      </a>
    );
  };

  return (
    <div className="border-input-border-gray absolute bottom-0 left-0 right-0 z-10 flex shrink-0 items-center justify-center gap-4 border-t bg-white px-[23px] pb-4 pt-6 shadow-[0_-2px_8px_rgba(0,0,0,0.1)] md:static md:items-end md:justify-end md:pb-0 md:shadow-none">
      {isProviderResource && finalDetectedProviderName && partnerResourceConfig
        ? renderProviderCta()
        : renderDefaultCta()}
      {resource.phone_number && displayPhoneNumber && (
        <a href={`tel:${resource.phone_number}`} onClick={onCallClick}>
          <button className="font-albert-sans bg-button-primary hover:bg-button-primary-hover flex h-10 min-w-36 cursor-pointer items-center justify-center rounded-full px-7 py-2.5 text-base font-medium leading-5 text-white transition-colors">
            Call
          </button>
        </a>
      )}
    </div>
  );
};
